import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { MetricStrip } from '@/components/shared/MetricStrip';
import { api, type TimelineResponse, type TaskResponse, type TimelineTaskResponse } from '@/api/api';
import { projectKeys, projectMembersOptions, projectTagsOptions } from '@/features/projects/api';
import { meOptions } from '@/features/auth/api';
import { timelineKeys, timelineOptions } from '@/features/timeline/api';
import { holidayKeys, holidaysOptions } from '@/features/holidays/api';
import { taskKeys, taskStatusOptions, useTaskStatuses } from '@/features/tasks/api';
import { useTaskForm } from '@/features/tasks/task-form-context';
import { workloadKeys } from '@/features/workload/api';
import {
  TimelineGantt,
  type TimelineGanttHandle,
  type TimelineMoveInput,
} from '@/features/timeline/components/TimelineGantt';
import { TimelineToolbar } from '@/features/timeline/components/TimelineToolbar';
import { DeploymentDrawer } from '@/features/timeline/components/DeploymentDrawer';
import { EpicDrawer } from '@/features/epics/components/EpicDrawer';
import { CreateEpicDialog } from '@/features/epics/components/CreateEpicDialog';
import { SummaryReportDialog } from '@/features/summary';
import { ImportHolidaysDialog } from '@/features/holidays/components/ImportHolidaysDialog';
import { usePermission, useIsProjectAdmin } from '@/features/auth/use-permission';
import { buildTimelineMetrics } from '@/features/timeline/metrics';
import {
  defaultTimelineWindow,
  deriveProjectStart,
} from '@/features/timeline/window';
import { isTaskDone } from '@/lib/status-kind';
import { surfaceApiError } from '@/lib/api-error';
import type { TimelineZoom } from '@/components/shared/TimelineZoomSwitcher';

// Multi-select facets can arrive as a lone value — an old single-select URL, a
// hand-typed ?tag_id=x, or a serializer that flattens a one-element array back
// to a string. Normalize to an array before validating so the filters hold.
// (Same shape as Tasks — Timeline mirrors its Tag/PIC filtering.)
const toArray = (v: unknown): unknown => (v === undefined ? undefined : Array.isArray(v) ? v : [v]);

const timelineSearchSchema = z.object({
  zoom: z.enum(['day', 'week', 'month']).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  // Multi-select, applied client-side over the whole timeline graph (like
  // Tasks §6.2) — the single-value URLs from the old server-side tag filter
  // still parse via `toArray`.
  tag_id: z.preprocess(toArray, z.array(z.string()).optional()),
  pic: z.preprocess(toArray, z.array(z.string()).optional()),
  // Hide completed (Done) task rows. A render-only *view* filter (like sort) —
  // absent/false shows everything. `.catch` guards against hand-edited URLs.
  hide_done: z.boolean().optional().catch(undefined),
  // Auto-sort is a toggleable *view*; absent means the manual (DB) order.
  sort: z.enum(['start_date', 'name']).optional(),
});

export type TimelineSearch = z.infer<typeof timelineSearchSchema>;

export const Route = createFileRoute('/_authed/projects/$projectId/timeline')({
  validateSearch: timelineSearchSchema,
  // The gantt renders across the full project span and scrolls horizontally, so
  // we fetch the whole timeline once. Tag/PIC filtering is applied client-side
  // (like Tasks), so the fetch takes no filter args and the URL `from`/`to` (if
  // present) become the initial scroll target.
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectMembersOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectTagsOptions(params.projectId)),
      context.queryClient.ensureQueryData(holidaysOptions(params.projectId)),
      context.queryClient.ensureQueryData(timelineOptions(params.projectId)),
      context.queryClient.ensureQueryData(taskStatusOptions(params.projectId)),
    ]),
  component: TimelinePage,
});

function flattenTasks(tasks: TimelineTaskResponse[]): TimelineTaskResponse[] {
  return tasks.flatMap((t) => [t, ...flattenTasks(t.sub_tasks ?? [])]);
}

function TimelinePage() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { openEdit } = useTaskForm();

  const { data: timeline } = useSuspenseQuery(timelineOptions(projectId));
  const { data: members } = useSuspenseQuery(projectMembersOptions(projectId));
  const { data: tags } = useSuspenseQuery(projectTagsOptions(projectId));
  const { data: me } = useSuspenseQuery(meOptions());
  const { data: holidays } = useSuspenseQuery(holidaysOptions(projectId));
  const { statusesById } = useTaskStatuses(projectId);

  const { holidayDates, holidayNames, holidayIdsByDate } = useMemo(() => {
    const dates = new Set<string>();
    const names = new Map<string, string>();
    const ids = new Map<string, string>();
    for (const h of holidays) {
      dates.add(h.date);
      ids.set(h.date, h.id);
      if (h.name) names.set(h.date, h.name);
    }
    return { holidayDates: dates, holidayNames: names, holidayIdsByDate: ids };
  }, [holidays]);

  const zoom: TimelineZoom = search.zoom ?? 'week';
  const projectStart = useMemo(() => deriveProjectStart(timeline), [timeline]);
  const window = useMemo(() => {
    if (search.from && search.to) return { from: search.from, to: search.to };
    return defaultTimelineWindow(zoom, projectStart);
  }, [search.from, search.to, zoom, projectStart]);

  const metrics = useMemo(() => {
    const isDone = (statusId: string) => isTaskDone(statusId, statusesById);
    return buildTimelineMetrics(timeline, isDone);
  }, [timeline, statusesById]);

  const memberDisplayNames = useMemo(() => {
    const map: Record<string, string> = {};
    if (me) map[me.id] = me.display_name;
    for (const m of members) {
      if (!map[m.user_id]) map[m.user_id] = m.display_name;
    }
    return map;
  }, [members, me]);

  const tagOptions = useMemo(
    () => tags.map((t) => ({ value: t.id, label: t.name })),
    [tags],
  );
  const picOptions = useMemo(
    () =>
      members.map((m) => ({
        value: m.user_id,
        label: memberDisplayNames[m.user_id] ?? m.display_name,
      })),
    [members, memberDisplayNames],
  );

  const selectedTagIds = search.tag_id ?? [];
  const selectedPicIds = search.pic ?? [];
  const hideDone = search.hide_done ?? false;

  // Tag/PIC are multi-select and applied client-side over the whole graph, the
  // same rule as Tasks (§6.2): within a facet an item passes if it matches ANY
  // selected value, and must pass every active facet. Tasks are filtered by
  // their own tag_ids / assignees; an epic is kept if it still has a visible
  // task, or (tag-only) the epic itself carries a selected tag. The underlying
  // data is never mutated — this is a render-only view over copies.
  const filteredTimeline = useMemo(() => {
    const tagSet = new Set(search.tag_id ?? []);
    const picSet = new Set(search.pic ?? []);
    if (tagSet.size === 0 && picSet.size === 0) return timeline;
    const epics = [];
    for (const epic of timeline.epics) {
      const epicMatchesTag = tagSet.size > 0 && epic.tag_ids.some((id) => tagSet.has(id));
      const tasks = epic.tasks.filter((t) => {
        const okTag = tagSet.size === 0 || epicMatchesTag || t.tag_ids.some((id) => tagSet.has(id));
        const okPic = picSet.size === 0 || t.assignees.some((a) => picSet.has(a.user_id));
        return okTag && okPic;
      });
      // Keep an empty epic only when a tag filter matched it and no PIC filter
      // is narrowing to a person (epics have no assignees).
      if (tasks.length > 0 || (picSet.size === 0 && epicMatchesTag)) {
        epics.push({ ...epic, tasks });
      }
    }
    return { ...timeline, epics };
  }, [timeline, search.tag_id, search.pic]);

  const updateSearch = (next: Partial<TimelineSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  const ganttRef = useRef<TimelineGanttHandle>(null);

  const handleResetWindow = () => {
    ganttRef.current?.scrollToToday();
  };

  const handleZoomChange = (next: TimelineZoom) => {
    const w = defaultTimelineWindow(next, projectStart);
    updateSearch({ zoom: next, from: w.from, to: w.to });
  };

  // Multi-select — store the whole selection (empty clears the facet).
  const handleTagChange = (vals: string[]) =>
    updateSearch({ tag_id: vals.length > 0 ? vals : undefined });
  const handlePicChange = (vals: string[]) =>
    updateSearch({ pic: vals.length > 0 ? vals : undefined });

  const [openDeploymentId, setOpenDeploymentId] = useState<string | null>(null);
  const [openEpicId, setOpenEpicId] = useState<string | null>(null);
  const [createEpicOpen, setCreateEpicOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [importHolidaysOpen, setImportHolidaysOpen] = useState(false);
  const openEpic =
    openEpicId !== null ? (timeline.epics.find((e) => e.id === openEpicId) ?? null) : null;
  const openDeployment =
    openDeploymentId !== null
      ? (timeline.deployments.find((d) => d.id === openDeploymentId) ?? null)
      : null;
  const canCreateEpic = usePermission(projectId, 'CreateEpic');
  const canCreateTask = usePermission(projectId, 'CreateTask');
  const isAdmin = useIsProjectAdmin(projectId);
  const onCreateEpic = canCreateEpic ? () => setCreateEpicOpen(true) : undefined;

  // Drag-to-reschedule: optimistic patch on the timeline cache, then settle by
  // invalidating the cross-view trio (CLAUDE §8.2) so Tasks and Workload pick
  // up the new dates without a refetch round-trip on the timeline itself.
  const invalidateAfterMove = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: timelineKeys.all }),
      queryClient.invalidateQueries({ queryKey: taskKeys.all }),
      queryClient.invalidateQueries({ queryKey: workloadKeys.all }),
    ]);

  const moveTask = useMutation({
    mutationFn: (input: { taskId: string } & TimelineMoveInput) =>
      api.updateTask(input.taskId, {
        start_date: input.startDate,
        end_date: input.endDate,
      }),
    onMutate: async ({ taskId, startDate, endDate }) => {
      await queryClient.cancelQueries({ queryKey: timelineKeys.all });
      const snapshot = queryClient.getQueriesData<TimelineResponse>({
        queryKey: timelineKeys.all,
      });
      for (const [key, prev] of snapshot) {
        if (!prev) continue;
        queryClient.setQueryData<TimelineResponse>(key, {
          ...prev,
          epics: prev.epics.map((ep) => ({
            ...ep,
            tasks: ep.tasks.map((t) =>
              t.id === taskId ? { ...t, start_date: startDate, end_date: endDate } : t,
            ),
          })),
        });
      }
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      if (ctx) for (const [key, data] of ctx.snapshot) queryClient.setQueryData(key, data);
      surfaceApiError(err);
    },
    onSettled: () => {
      void invalidateAfterMove();
    },
  });

  const moveEpic = useMutation({
    mutationFn: (input: { epicId: string } & TimelineMoveInput) =>
      api.updateEpic(input.epicId, {
        start_date: input.startDate,
        end_date: input.endDate,
      }),
    onMutate: async ({ epicId, startDate, endDate }) => {
      await queryClient.cancelQueries({ queryKey: timelineKeys.all });
      const snapshot = queryClient.getQueriesData<TimelineResponse>({
        queryKey: timelineKeys.all,
      });
      for (const [key, prev] of snapshot) {
        if (!prev) continue;
        queryClient.setQueryData<TimelineResponse>(key, {
          ...prev,
          epics: prev.epics.map((ep) =>
            ep.id === epicId ? { ...ep, start_date: startDate, end_date: endDate } : ep,
          ),
        });
      }
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      if (ctx) for (const [key, data] of ctx.snapshot) queryClient.setQueryData(key, data);
      surfaceApiError(err);
    },
    onSettled: () => {
      void Promise.all([
        invalidateAfterMove(),
        queryClient.invalidateQueries({ queryKey: projectKeys.epics(projectId) }),
      ]);
    },
  });

  // Manual reorder (drag in the name column). Optimistically reorders the
  // timeline cache by the new id list, then settles on the timeline query only —
  // ordering doesn't change dates/assignees, so the cross-view trio is untouched.
  const applyOrder = <T extends { id: string }>(items: T[], orderedIds: string[]): T[] => {
    const byId = new Map(items.map((it) => [it.id, it]));
    const next = orderedIds.map((id) => byId.get(id)).filter((it): it is T => it !== undefined);
    // Keep any ids not present in the order list (defensive) appended as-is.
    for (const it of items) if (!orderedIds.includes(it.id)) next.push(it);
    return next;
  };

  const reorderEpics = useMutation({
    mutationFn: (epicIds: string[]) => api.reorderEpics(projectId, epicIds),
    onMutate: async (epicIds) => {
      await queryClient.cancelQueries({ queryKey: timelineKeys.all });
      const snapshot = queryClient.getQueriesData<TimelineResponse>({ queryKey: timelineKeys.all });
      for (const [key, prev] of snapshot) {
        if (!prev) continue;
        queryClient.setQueryData<TimelineResponse>(key, {
          ...prev,
          epics: applyOrder(prev.epics, epicIds),
        });
      }
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      if (ctx) for (const [key, data] of ctx.snapshot) queryClient.setQueryData(key, data);
      surfaceApiError(err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: timelineKeys.all });
    },
  });

  const reorderTasks = useMutation({
    mutationFn: (input: { epicId: string; taskIds: string[] }) =>
      api.reorderTasks(input.epicId, input.taskIds),
    onMutate: async ({ epicId, taskIds }) => {
      await queryClient.cancelQueries({ queryKey: timelineKeys.all });
      const snapshot = queryClient.getQueriesData<TimelineResponse>({ queryKey: timelineKeys.all });
      for (const [key, prev] of snapshot) {
        if (!prev) continue;
        queryClient.setQueryData<TimelineResponse>(key, {
          ...prev,
          epics: prev.epics.map((ep) =>
            ep.id === epicId ? { ...ep, tasks: applyOrder(ep.tasks, taskIds) } : ep,
          ),
        });
      }
      return { snapshot };
    },
    onError: (err, _vars, ctx) => {
      if (ctx) for (const [key, data] of ctx.snapshot) queryClient.setQueryData(key, data);
      surfaceApiError(err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: timelineKeys.all });
    },
  });

  // Holiday capacity affects workload utilization (see product_doc/holiday.md
  // §5) — invalidate the cross-view trio plus the holiday list itself so the
  // day-zoom tint, red-MD signals on Tasks, and Workload heatmap all settle
  // from a single PM action.
  const invalidateAfterHolidayChange = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: holidayKeys.byProject(projectId) }),
      queryClient.invalidateQueries({ queryKey: timelineKeys.all }),
      queryClient.invalidateQueries({ queryKey: taskKeys.all }),
      queryClient.invalidateQueries({ queryKey: workloadKeys.all }),
    ]);

  const markHoliday = useMutation({
    mutationFn: (date: string) => api.createHoliday(projectId, { date }),
    onError: surfaceApiError,
    onSettled: () => {
      void invalidateAfterHolidayChange();
    },
  });

  const removeHoliday = useMutation({
    mutationFn: (holidayId: string) => api.deleteHoliday(holidayId),
    onError: surfaceApiError,
    onSettled: () => {
      void invalidateAfterHolidayChange();
    },
  });

  const onMarkHoliday = isAdmin ? (date: string) => markHoliday.mutate(date) : undefined;
  const onRemoveHoliday = isAdmin
    ? (holidayId: string) => removeHoliday.mutate(holidayId)
    : undefined;

  return (
    <div className="flex h-full flex-col">
      <MetricStrip metrics={metrics} />
      <TimelineToolbar
        zoom={zoom}
        onZoomChange={handleZoomChange}
        onResetWindow={handleResetWindow}
        tagOptions={tagOptions}
        selectedTagIds={selectedTagIds}
        onTagChange={handleTagChange}
        picOptions={picOptions}
        selectedPicIds={selectedPicIds}
        onPicChange={handlePicChange}
        hideDone={hideDone}
        onHideDoneChange={(next) => updateSearch({ hide_done: next || undefined })}
        onImportHolidays={isAdmin ? () => setImportHolidaysOpen(true) : undefined}
        onOpenSummaryReport={() => setSummaryOpen(true)}
      />
      <div className="flex-1 min-h-0">
      <TimelineGantt
        ref={ganttRef}
        projectId={projectId}
        data={filteredTimeline}
        window={window}
        zoom={zoom}
        statusesById={statusesById}
        memberDisplayNames={memberDisplayNames}
        hideDone={hideDone}
        onDeploymentClick={(id) => setOpenDeploymentId(id)}
        onEpicClick={(id) => setOpenEpicId(id)}
        onTaskClick={(id) => {
          const task = flattenTasks(timeline.epics.flatMap((e) => e.tasks)).find((t) => t.id === id);
          if (task) openEdit(task as unknown as TaskResponse);
        }}
        onCreateEpic={onCreateEpic}
        canEditTask={canCreateTask}
        canEditEpic={canCreateEpic}
        onMoveTask={(taskId, next) => moveTask.mutate({ taskId, ...next })}
        onMoveEpic={(epicId, next) => moveEpic.mutate({ epicId, ...next })}
        sort={search.sort}
        onSortChange={(next) => updateSearch({ sort: next })}
        canReorder={canCreateEpic || canCreateTask}
        onReorderEpics={(epicIds) => reorderEpics.mutate(epicIds)}
        onReorderTasks={(epicId, taskIds) => reorderTasks.mutate({ epicId, taskIds })}
        holidayDates={holidayDates}
        holidayNames={holidayNames}
        holidayIdsByDate={holidayIdsByDate}
        onMarkHoliday={onMarkHoliday}
        onRemoveHoliday={onRemoveHoliday}
      />
      </div>
      <DeploymentDrawer
        projectId={projectId}
        deployment={openDeployment}
        memberDisplayNames={memberDisplayNames}
        onClose={() => setOpenDeploymentId(null)}
        onTaskClick={openEdit}
      />
      <EpicDrawer
        projectId={projectId}
        epic={openEpic}
        taskCount={openEpic?.tasks.length}
        tags={tags}
        onClose={() => setOpenEpicId(null)}
      />
      <CreateEpicDialog
        projectId={projectId}
        tags={tags}
        open={createEpicOpen}
        onClose={() => setCreateEpicOpen(false)}
      />
      <SummaryReportDialog
        projectId={projectId}
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
      />
      <ImportHolidaysDialog
        projectId={projectId}
        open={importHolidaysOpen}
        onClose={() => setImportHolidaysOpen(false)}
        onImported={() => {
          setImportHolidaysOpen(false);
          void invalidateAfterHolidayChange();
        }}
      />
    </div>
  );
}
